/**
 * Mural: um quadro branco de anotações. Clicou no vazio, nasce um post-it ali e você já escreve.
 * Clicou no post-it, aparecem as ferramentas (cor, checklist, lembrete, pessoa, vínculo, anexo, mais).
 * Informal de propósito: prazo processual é atividade ("Virar atividade").
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { navigate, useSearch, useTitle } from "@/lib/router";
import { useLookups } from "@/lib/catalog";
import { EntityPicker, ErrorBox, Field, FormGrid, Loading, Modal, UnitSelect, UserSelect, useAct, type EntityRef } from "@/ui/kit";
import { Avatar } from "@/ui/icons";
import { confirmar, toast } from "@/ui/feedback";
import { NewActivityModal } from "./ActivityPages";

interface Access { view: boolean; edit: boolean; manage: boolean }
interface Board { id: string; name: string; kind: "pessoal" | "compartilhado" | "escritorio" | "unidade"; unitId: string | null; ownerUserId: string; layout: string; members: Array<{ userId: string; canEdit: boolean }>; access: Access; _count?: { notes: number } }
interface Note {
  id: string; boardId: string; text: string; color: string; checklist: Array<{ t: string; d: boolean }> | null; x: number; y: number; w: number; h: number;
  pinned: boolean; done: boolean; archived: boolean; remindAt: string | null; responsibleUserId: string | null;
  activityId: string | null; createdBy: string; source: string;
  attachments: Array<{ id: string; name: string; mimeType: string }>; ref: EntityRef | null; activity: { id: string; number: number; state: string } | null;
}
const COLORS: Array<[string, string]> = [["amarelo", "#FFF4B3"], ["rosa", "#FFD6E0"], ["azul", "#D6EBFF"], ["verde", "#D9F5D6"], ["laranja", "#FFE0C2"], ["lilas", "#E8DCFF"], ["cinza", "#ECEEF1"]];
const KIND_LABEL = { pessoal: "Pessoal", compartilhado: "Compartilhado", escritorio: "Escritório", unidade: "Unidade" };
const brWhen = (iso: string) => new Date(iso).toLocaleString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const toLocalInput = (iso: string | null) => (iso ? new Date(new Date(iso).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "");
const looksLegal = (t: string) => /\b(prazo|audi[eê]ncia|intima[cç])/i.test(t);
/** Leve inclinação de papel, estável por post-it. */
const tilt = (id: string) => { let h = 0; for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0; return ((h % 5) - 2) * 0.6; };

export function MuralPage(): JSX.Element {
  useTitle("Mural");
  const [search, setSearch] = useSearch();
  const qc = useQueryClient();
  const { data: boards, error } = useQuery({ queryKey: ["mural-quadros"], queryFn: () => api.get<Board[]>("/mural/quadros") });
  const boardId = search.get("quadro") ?? boards?.[0]?.id ?? null;
  const board = boards?.find((b) => b.id === boardId) ?? null;
  const [boardDlg, setBoardDlg] = useState<"novo" | "editar" | null>(null);
  const [archived, setArchived] = useState(false);
  const [focus, setFocus] = useState<string | null>(null);
  const [actFor, setActFor] = useState<Note | null>(null);

  // Abrir direto um post-it (?nota=…, vindo do Telegram, do sino ou do Início).
  const noteParam = search.get("nota");
  useEffect(() => {
    if (!noteParam) return;
    api.get<Note>(`/mural/notas/${noteParam}`).then((n) => {
      const wantAct = search.get("atividade") === "1";
      setSearch({ quadro: n.boardId, nota: null, atividade: null });
      if (wantAct) setActFor(n); else setFocus(n.id);
    }).catch((e) => toast.error((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteParam]);

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="wb-top">
        <div className="wb-tabs">
          {(boards ?? []).map((b) => (
            <button key={b.id} className={`wb-tab ${b.id === boardId ? "on" : ""}`} onClick={() => { setSearch({ quadro: b.id }); setArchived(false); }} title={KIND_LABEL[b.kind]}>
              {b.kind === "pessoal" ? "🗒️" : b.kind === "compartilhado" ? "👥" : "🏢"} {b.name}
            </button>
          ))}
          <button className="wb-tab add" onClick={() => setBoardDlg("novo")} title="Novo mural">＋</button>
        </div>
        <span className="spacer" />
        <span className="muted small">Dois cliques no vazio para anotar · clique no post-it para cor, checklist, lembrete…</span>
        <button className="link-btn small" onClick={() => setArchived(!archived)}>{archived ? "voltar ao mural" : "arquivados"}</button>
        {board?.access.manage && <button className="link-btn small" onClick={() => setBoardDlg("editar")}>mural ⚙</button>}
      </div>
      <ErrorBox error={error} />
      {!boards ? <Loading /> : board && (archived
        ? <Archived board={board} />
        : <Whiteboard key={board.id} board={board} boards={boards} focus={focus} onActivity={setActFor} />)}
      {boardDlg && <BoardModal board={boardDlg === "editar" ? board ?? undefined : undefined} onClose={() => setBoardDlg(null)} onSaved={(id) => { setBoardDlg(null); setSearch({ quadro: id }); }} />}
      {actFor && (
        <NewActivityModal
          defaults={{ ctx: actFor.ref ?? undefined, title: actFor.text.split("\n")[0]!.slice(0, 200), description: actFor.text }}
          onClose={() => setActFor(null)}
          onCreated={async (id) => {
            await api.patch(`/mural/notas/${actFor.id}`, { activityId: id });
            await qc.invalidateQueries({ queryKey: ["mural-notas"] });
            toast.ok("Atividade criada a partir do post-it");
          }} />
      )}
    </div>
  );
}

// ------------------------------------------------------------ quadro branco

function Whiteboard({ board, boards, focus, onActivity }: { board: Board; boards: Board[]; focus: string | null; onActivity: (n: Note) => void }): JSX.Element {
  const qc = useQueryClient();
  const key = ["mural-notas", board.id, false];
  const { data } = useQuery({ queryKey: key, queryFn: () => api.get<{ notes: Note[]; access: Access }>(`/mural/quadros/${board.id}/notas`), refetchInterval: 60_000 });
  const area = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ x: number; y: number } | null>(null);
  const canEdit = board.access.edit;
  useEffect(() => {
    if (!focus) return;
    setOpen(focus);
    setTimeout(() => document.getElementById(`note-${focus}`)?.scrollIntoView({ block: "center", behavior: "smooth" }), 200);
  }, [focus]);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["mural-notas", board.id] }); void qc.invalidateQueries({ queryKey: ["mural-meus"] }); void qc.invalidateQueries({ queryKey: ["mural-quadros"] }); };

  const patch = async (id: string, p: Record<string, unknown>) => {
    qc.setQueryData<{ notes: Note[]; access: Access }>(key, (d) => d && { ...d, notes: d.notes.map((n) => (n.id === id ? { ...n, ...p } as Note : n)) });
    try { await api.patch(`/mural/notas/${id}`, p); } catch (e) { toast.error((e as Error).message); }
    refresh();
  };
  const create = async (text: string) => {
    const d = draft; setDraft(null);
    if (!d || !text.trim()) return;
    try { await api.post<Note>("/mural/notas", { boardId: board.id, text: text.trim(), x: d.x, y: d.y }); refresh(); }
    catch (e) { toast.error((e as Error).message); }
  };
  // Duplo clique no vazio cria o post-it (clique simples não faz nada, para não criar sem querer).
  const onBoardDblClick = (e: React.MouseEvent) => {
    const t = e.target as HTMLElement;
    const empty = t === area.current || t.classList.contains("wb-size") || t.classList.contains("wb-empty");
    if (!empty || !canEdit || draft) return;
    window.getSelection()?.removeAllRanges();
    const r = area.current!.getBoundingClientRect();
    setDraft({ x: Math.max(0, Math.round(e.clientX - r.left + area.current!.scrollLeft - 20)), y: Math.max(0, Math.round(e.clientY - r.top + area.current!.scrollTop - 20)) });
  };
  const notes = data?.notes ?? [];
  const h = Math.max(640, ...notes.map((n) => n.y + n.h + 200), (draft?.y ?? 0) + 400);
  const w = Math.max(...notes.map((n) => n.x + n.w + 100), 0);
  const current = notes.find((n) => n.id === open) ?? null;
  return (
    <div className={`wb ${canEdit ? "" : "ro"}`} ref={area} onDoubleClick={onBoardDblClick} style={{ height: "calc(100vh - 190px)" }}>
      <div className="wb-size" style={{ height: h, width: w || undefined }} />
      {!data && <div className="pad"><Loading /></div>}
      {data && !notes.length && !draft && <div className="wb-empty">{canEdit ? "Dê dois cliques em qualquer lugar para criar um post-it ✍️" : "Nenhum post-it aqui."}</div>}
      {notes.map((n) => <Sticky key={n.id} n={n} canEdit={canEdit} onOpen={() => setOpen(n.id)} onPatch={(p) => void patch(n.id, p)} />)}
      {draft && <DraftNote x={draft.x} y={draft.y} onDone={(t) => void create(t)} />}
      {current && <NoteSheet n={current} boards={boards} canEdit={canEdit} onClose={() => setOpen(null)} onSaved={refresh} onActivity={() => { setOpen(null); onActivity(current); }} />}
    </div>
  );
}

function DraftNote({ x, y, onDone }: { x: number; y: number; onDone: (text: string) => void }): JSX.Element {
  const [t, setT] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);
  const born = useRef(Date.now());
  useEffect(() => { const id = setTimeout(() => ref.current?.focus(), 0); return () => clearTimeout(id); }, []);
  return (
    <div className="sticky c-amarelo editing" style={{ left: x, top: y, width: 200, height: 170 }} onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
      <textarea ref={ref} className="sticky-text hand" value={t} placeholder="Escreva aqui…" onChange={(e) => setT(e.target.value)}
        onBlur={() => { if (Date.now() - born.current < 250 && !t) { ref.current?.focus(); return; } onDone(t); }}
        onKeyDown={(e) => { if (e.key === "Escape") onDone(""); if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onDone(t); }} />
      <div className="sticky-meta">clique fora para salvar · Esc cancela</div>
    </div>
  );
}

/** Post-it no quadro: arrastar move, canto ◢ aumenta, clique abre a tela do post-it. */
function Sticky({ n, canEdit, onOpen, onPatch }: { n: Note; canEdit: boolean; onOpen: () => void; onPatch: (p: Record<string, unknown>) => void }): JSX.Element {
  const { userName } = useLookups();
  const [pos, setPos] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const p = pos ?? { x: n.x, y: n.y, w: n.w, h: n.h };
  const drag = (e: React.PointerEvent, mode: "move" | "size") => {
    if ((e.target as HTMLElement).closest("input,a,button")) return;
    e.stopPropagation();
    if (!canEdit) return;
    const s = { px: e.clientX, py: e.clientY, ...p };
    let moved = false;
    const mv = (ev: PointerEvent) => {
      const dx = ev.clientX - s.px, dy = ev.clientY - s.py;
      if (Math.abs(dx) + Math.abs(dy) > 3) moved = true;
      if (!moved) return;
      setPos(mode === "move" ? { x: Math.max(0, s.x + dx), y: Math.max(0, s.y + dy), w: s.w, h: s.h } : { x: s.x, y: s.y, w: Math.max(140, s.w + dx), h: Math.max(110, s.h + dy) });
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up);
      if (!moved) return;
      const dx = ev.clientX - s.px, dy = ev.clientY - s.py;
      onPatch(mode === "move" ? { x: Math.round(Math.max(0, s.x + dx)), y: Math.round(Math.max(0, s.y + dy)) } : { w: Math.round(Math.min(900, Math.max(140, s.w + dx))), h: Math.round(Math.min(900, Math.max(110, s.h + dy))) });
      // Evita que o "clique" do fim do arraste abra a tela.
      dragged.current = true; setTimeout(() => { dragged.current = false; setPos(null); }, 300);
    };
    window.addEventListener("pointermove", mv); window.addEventListener("pointerup", up);
  };
  const dragged = useRef(false);
  const list = n.checklist ?? [];
  const late = n.remindAt && !n.done && new Date(n.remindAt) < new Date();
  return (
    <div id={`note-${n.id}`} className={`sticky c-${n.color} ${n.done ? "done" : ""}`} title="Clique para abrir · arraste para mover"
      style={{ left: p.x, top: p.y, width: p.w, height: p.h, transform: pos ? "none" : `rotate(${tilt(n.id)}deg)`, zIndex: pos ? 50 : n.pinned ? 20 : 10 }}
      onPointerDown={(e) => drag(e, "move")}
      onClick={(e) => { e.stopPropagation(); if (!dragged.current && !(e.target as HTMLElement).closest("input,a,button")) onOpen(); }}>
      <div className="sticky-text hand ro">{n.text}</div>
      {list.length > 0 && (
        <div className="sticky-check hand">
          {list.map((c, i) => (
            <label key={i} className="row nowrap" style={{ gap: 6 }} onClick={(e) => e.stopPropagation()}>
              <input type="checkbox" checked={c.d} disabled={!canEdit} onChange={(e) => onPatch({ checklist: list.map((x, j) => (j === i ? { ...x, d: e.target.checked } : x)) })} />
              <span style={{ textDecoration: c.d ? "line-through" : undefined }}>{c.t}</span>
            </label>
          ))}
        </div>
      )}
      <div className="sticky-meta">
        {n.remindAt && <span className={late ? "late" : ""}>⏰ {brWhen(n.remindAt)}</span>}
        {n.ref && <span>🔗 {n.ref.label}</span>}
        {n.activity && <span>⚖︎ atividade #{n.activity.number}</span>}
        {n.attachments.length > 0 && <span>📎 {n.attachments.length}</span>}
        {n.responsibleUserId && <span title={userName(n.responsibleUserId)}><Avatar name={userName(n.responsibleUserId)} id={n.responsibleUserId} size={18} /></span>}
      </div>
      {n.pinned && <span className="sticky-pin">📌</span>}
      {canEdit && <span className="sticky-resize" onPointerDown={(e) => drag(e, "size")} title="Puxe para aumentar">◢</span>}
    </div>
  );
}

/** Tela do post-it: tudo num lugar só (texto, cor, checklist, lembrete, pessoa, vínculo, anexos, ações). */
function NoteSheet({ n, boards, canEdit, onClose, onSaved, onActivity }: { n: Note; boards: Board[]; canEdit: boolean; onClose: () => void; onSaved: () => void; onActivity: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [f, setF] = useState({
    text: n.text, color: n.color, remindAt: toLocalInput(n.remindAt), responsibleUserId: n.responsibleUserId,
    checklist: n.checklist ?? [], ref: n.ref, pinned: n.pinned, boardId: n.boardId,
  });
  const [item, setItem] = useState("");
  const [busy, setBusy] = useState(false);
  const up = (p: Partial<typeof f>) => setF({ ...f, ...p });
  const quick = (when: "1h" | "18h" | "am") => {
    const d = new Date();
    if (when === "1h") d.setHours(d.getHours() + 1);
    if (when === "18h") { d.setHours(18, 0, 0, 0); if (d < new Date()) d.setDate(d.getDate() + 1); }
    if (when === "am") { d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); }
    up({ remindAt: toLocalInput(d.toISOString()) });
  };
  const send = async (extra: Record<string, unknown> = {}, msg = "Post-it salvo") => {
    if (!f.text.trim()) { toast.error("Escreva algo no post-it."); return; }
    setBusy(true);
    try {
      await api.patch(`/mural/notas/${n.id}`, {
        text: f.text, color: f.color, remindAt: f.remindAt ? new Date(f.remindAt).toISOString() : null, responsibleUserId: f.responsibleUserId,
        checklist: f.checklist.filter((c) => c.t.trim()).length ? f.checklist.filter((c) => c.t.trim()) : null, pinned: f.pinned,
        boardId: f.boardId !== n.boardId ? f.boardId : undefined,
        personId: f.ref?.kind === "pessoa" ? f.ref.id : null, caseId: f.ref?.kind === "caso" ? f.ref.id : null, processId: f.ref?.kind === "processo" ? f.ref.id : null,
        ...extra,
      });
      onSaved(); toast.ok(msg); onClose();
    } catch (e) { toast.error((e as Error).message); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!(await confirmar("Excluir este post-it?", { confirm: "Excluir", danger: true }))) return;
    try { await api.del(`/mural/notas/${n.id}`); onSaved(); onClose(); } catch (e) { toast.error((e as Error).message); }
  };
  const upload = async (file: File) => {
    const form = new FormData(); form.append("file", file);
    try { await api.upload(`/mural/notas/${n.id}/anexo`, form); await qc.invalidateQueries({ queryKey: ["mural-notas"] }); toast.ok("Anexo enviado"); }
    catch (e) { toast.error((e as Error).message); }
  };
  return (
    <div onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
      <Modal title="Post-it" onClose={onClose} wide footer={canEdit ? (
        <>
          <button className="btn ghost" onClick={() => void remove()}>Excluir</button>
          <button className="btn" onClick={() => void send({ archived: true }, "Post-it arquivado")}>Arquivar</button>
          <button className="btn" onClick={() => void send({ done: !n.done }, n.done ? "Reaberto" : "Concluído")}>{n.done ? "Reabrir" : "Concluir"}</button>
          {!n.activity && <button className="btn" onClick={onActivity}>⚖︎ Virar atividade</button>}
          <span className="spacer" />
          <button className="btn primary" disabled={busy} onClick={() => void send()}>Salvar</button>
        </>
      ) : <button className="btn" onClick={onClose}>Fechar</button>}>
        <div className="sheet">
          <div className={`sheet-note c-${f.color}`}>
            <textarea className="sticky-text hand" value={f.text} disabled={!canEdit} onChange={(e) => up({ text: e.target.value })} placeholder="Escreva aqui…" autoFocus />
            <div className="sticky-check hand" style={{ maxHeight: "none" }}>
              {f.checklist.map((c, i) => (
                <div key={i} className="row nowrap" style={{ gap: 6 }}>
                  <input type="checkbox" checked={c.d} disabled={!canEdit} onChange={(e) => up({ checklist: f.checklist.map((x, j) => (j === i ? { ...x, d: e.target.checked } : x)) })} />
                  <input className="sticky-item" value={c.t} disabled={!canEdit} onChange={(e) => up({ checklist: f.checklist.map((x, j) => (j === i ? { ...x, t: e.target.value } : x)) })} />
                  {canEdit && <button className="link-btn small" title="Tirar item" onClick={() => up({ checklist: f.checklist.filter((_, j) => j !== i) })}>✕</button>}
                </div>
              ))}
              {canEdit && <input className="sticky-item" placeholder="☐ + item de checklist (Enter)" value={item} onChange={(e) => setItem(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && item.trim()) { up({ checklist: [...f.checklist, { t: item.trim(), d: false }] }); setItem(""); } }} />}
            </div>
          </div>
          <div className="sheet-fields">
            {looksLegal(f.text) && !n.activity && <div className="warn-box small">Parece prazo ou audiência. Prazo processual deve ser <b>atividade</b>: use “Virar atividade”.</div>}
            {n.activity && <div className="info-box small">Virou a atividade <a onClick={() => navigate(`/atividades/${n.activity!.id}`)}>#{n.activity.number}</a>.</div>}
            <Field label="Cor">
              <div className="row">{COLORS.map(([k, c]) => <span key={k} className="swatch" style={{ background: c, outline: f.color === k ? "2px solid var(--text)" : undefined }} onClick={() => canEdit && up({ color: k })} />)}</div>
            </Field>
            <Field label="Lembrete (sino e Telegram)">
              <div className="stack" style={{ gap: 6 }}>
                <div className="row">
                  <button className="chip" disabled={!canEdit} onClick={() => quick("1h")}>Em 1 hora</button>
                  <button className="chip" disabled={!canEdit} onClick={() => quick("18h")}>Hoje 18h</button>
                  <button className="chip" disabled={!canEdit} onClick={() => quick("am")}>Amanhã 9h</button>
                  {f.remindAt && <button className="link-btn small" onClick={() => up({ remindAt: "" })}>tirar</button>}
                </div>
                <input type="datetime-local" className="input" value={f.remindAt} disabled={!canEdit} onChange={(e) => up({ remindAt: e.target.value })} />
              </div>
            </Field>
            <Field label="Responsável"><UserSelect value={f.responsibleUserId} onChange={(v) => up({ responsibleUserId: v })} emptyLabel="Ninguém" /></Field>
            <Field label="Vinculado a (opcional)"><EntityPicker kinds={["processo", "caso", "pessoa"]} value={f.ref} onChange={(v) => up({ ref: v })} /></Field>
            <Field label="Mural">
              <select className="input" value={f.boardId} disabled={!canEdit} onChange={(e) => up({ boardId: e.target.value })}>
                {boards.filter((b) => b.access.edit).map((b) => <option key={b.id} value={b.id}>{b.name} ({KIND_LABEL[b.kind]})</option>)}
              </select>
            </Field>
            <Field label="Anexos">
              <div className="row">
                {n.attachments.map((a) => <button key={a.id} className="btn small" onClick={() => void api.download(`/documentos/${a.id}/download`, a.name, /^image\/|pdf/.test(a.mimeType))}>📎 {a.name}</button>)}
                {canEdit && <label className="btn small">+ anexar<input type="file" hidden onChange={(e) => { const fl = e.target.files?.[0]; if (fl) void upload(fl); e.target.value = ""; }} /></label>}
              </div>
            </Field>
            <label className="row nowrap"><input type="checkbox" checked={f.pinned} disabled={!canEdit} onChange={(e) => up({ pinned: e.target.checked })} /> Fixar por cima dos outros</label>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Archived({ board }: { board: Board }): JSX.Element {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["mural-notas", board.id, true], queryFn: () => api.get<{ notes: Note[] }>(`/mural/quadros/${board.id}/notas?arquivadas=1`) });
  if (!data) return <Loading />;
  if (!data.notes.length) return <div className="card muted small">Nada arquivado neste mural.</div>;
  return (
    <div className="mini-notes">
      {data.notes.map((n) => (
        <div key={n.id} className={`mini-note c-${n.color}`}>
          <div className="t hand">{n.text}</div>
          {board.access.edit && <button className="link-btn small" onClick={async () => { await api.patch(`/mural/notas/${n.id}`, { archived: false }); await qc.invalidateQueries({ queryKey: ["mural-notas", board.id] }); }}>desarquivar</button>}
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------ criar/editar mural

function BoardModal({ board, onClose, onSaved }: { board?: Board; onClose: () => void; onSaved: (id: string) => void }): JSX.Element {
  const { can, catalog, me } = useLookups();
  const [f, setF] = useState({ name: board?.name ?? "", kind: board?.kind ?? "compartilhado", unitId: board?.unitId ?? null as string | null, members: board?.members ?? [] as Array<{ userId: string; canEdit: boolean }> });
  const body = { name: f.name, members: f.kind === "compartilhado" ? f.members : undefined };
  const save = useAct(async () => board
    ? (await api.patch<{ id: string }>(`/mural/quadros/${board.id}`, body))
    : (await api.post<{ id: string }>("/mural/quadros", { ...body, kind: f.kind, unitId: f.kind === "unidade" ? f.unitId : null, layout: "livre" })),
  [["mural-quadros"], ["mural-notas"]], (r) => onSaved(r.id), board ? "Mural salvo" : "Mural criado");
  const archive = useAct(() => api.patch(`/mural/quadros/${board!.id}`, { archived: true }), [["mural-quadros"]], () => { onClose(); navigate("/mural"); }, "Mural arquivado");
  const others = (catalog?.users ?? []).filter((u) => u.id !== (board?.ownerUserId ?? me?.id));
  return (
    <Modal title={board ? "Mural" : "Novo mural"} onClose={onClose} footer={
      <>
        {board && board.kind !== "pessoal" && <button className="btn ghost" onClick={() => archive.mutate()}>Arquivar mural</button>}
        <button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn primary" disabled={!f.name.trim() || save.isPending} onClick={() => save.mutate()}>Salvar</button>
      </>
    }>
      <FormGrid cols={2}>
        <Field label="Nome" span={2}><input className="input" value={f.name} maxLength={80} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus placeholder="Ex.: Equipe trabalhista" /></Field>
        {!board && (
          <Field label="Quem vê">
            <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as Board["kind"] })}>
              <option value="compartilhado">Eu e quem eu escolher</option>
              <option value="pessoal">Só eu</option>
              {can("config.gerenciar") && <option value="escritorio">Todo o escritório</option>}
              {can("config.gerenciar") && <option value="unidade">Uma unidade</option>}
            </select>
          </Field>
        )}
        {!board && f.kind === "unidade" && <Field label="Unidade"><UnitSelect value={f.unitId} onChange={(v) => setF({ ...f, unitId: v })} /></Field>}
        {f.kind === "compartilhado" && (
          <Field label="Pessoas" span={2}>
            <div className="member-list">
              {others.map((u) => {
                const m = f.members.find((x) => x.userId === u.id);
                return (
                  <div key={u.id} className="row nowrap">
                    <label className="row nowrap grow"><input type="checkbox" checked={!!m} onChange={(e) => setF({ ...f, members: e.target.checked ? [...f.members, { userId: u.id, canEdit: true }] : f.members.filter((x) => x.userId !== u.id) })} /> {u.displayName}</label>
                    {m && <select className="input" style={{ width: 120 }} value={m.canEdit ? "e" : "v"} onChange={(e) => setF({ ...f, members: f.members.map((x) => (x.userId === u.id ? { ...x, canEdit: e.target.value === "e" } : x)) })}><option value="e">pode editar</option><option value="v">só vê</option></select>}
                  </div>
                );
              })}
            </div>
          </Field>
        )}
      </FormGrid>
      <ErrorBox error={save.error ?? archive.error} />
    </Modal>
  );
}
